import {
  Constants,
  Workflow,
  SubmittalDoc,
  SubmittalRevision,
  WorkflowState,
  WorkflowStateComplete,
  SubmittalCommonUtils,
  SubmittalFeedItemDoc,
  FeedItemSubType,
  DateUtils,
  Calendar,
} from 'common';
import { isUndefined } from 'lodash';
import { ConcourseDb } from 'src/global';

/**
 * Parsing, sequencing, and formatting of submittal identifier numbers.
 */
export class SubmittalUtils extends SubmittalCommonUtils {
  static DEFAULT_SUBMITTAL_TYPES: string[] = ['Shop Drawing', 'Product Data', 'Sample', 'Document', 'Test Report', 'Closeout', 'Other'];
  static DEFAULT_SUBMITTAL_WORKFLOW: Workflow = {
    id: 'default',
    name: 'Default Submittal Workflow',
    states: [
      {
        id: Constants.SUBMITTAL_CREATE_STATUS_ID,
        pastTense: 'Created',
        action: 'create',
        dueDays: 0,
        dueBy: [],
      },
      {
        id: Constants.SUBMITTAL_SUBMIT_STATUS_ID,
        pastTense: 'Submitted',
        action: 'Submit',
        dueDays: 10,
        dueBy: [],
      },
      {
        id: Constants.SUBMITTAL_APPROVAL_STATUS_ID,
        pastTense: 'Approved',
        action: 'Approve',
        dueDays: 5,
        dueBy: [],
      },
    ],
  };

  static TABLE_COLUMNS = [
    { key: 'number', label: 'Number' },
    { key: 'title', label: 'Title' },
    { key: 'division', label: 'Division' },
    { key: 'specSection', label: 'Spec Section' },
    { key: 'submittalType', label: 'Type' },
    { key: 'packageTitle', label: 'Package' },
    { key: 'status', label: 'Status' },
    { key: 'assignee', label: 'Assignee' },
    { key: 'statusDueDate', label: 'Assignee Due Date' },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'workflowDueDate', label: 'Workflow Due Date' },
    { key: 'lateDays', label: 'Late Days' },
  ];

  static getWriteRoles(projectId: string, submittalId: string): string[] {
    return [`p-${projectId}-submittals-${submittalId}-write`, `p-${projectId}-submittals-write`, `p-${projectId}-write`];
  }

  static getReadRoles(projectId: string, submittalId: string): string[] {
    return [...this.getWriteRoles(projectId, submittalId), `p-${projectId}-submittals-${submittalId}`, `p-${projectId}-submittals`, `p-${projectId}`];
  }

  static createDraftSubmittal(userId: string, code: string, workflow: Workflow, calendar: Calendar): SubmittalDoc {
    const dueDays = workflow.states.reduce((sum, st) => sum + st.dueDays, 0);
    const dueDate = new Date(DateUtils.currentDate.getTime() + dueDays * DateUtils.MILLIS_PER_DAY).toISOString();
    return {
      _id: ConcourseDb.makeShortDocId(),
      code: code,
      spec_section: null,
      cost_code: null,
      title: null,
      type: null,
      package: null,
      description: null,
      due_date: dueDate,
      distribution: [],
      successors: [],
      tags: [],
      revisions: [
        {
          num: 0,
          states: [...workflow.states],
          accepted_answers: [],
          created_by: userId,
          created_date: DateUtils.currentIso,
        },
      ],
    };
  }

  /**
   * Searches the submittals for the max number and then returns its components.
   * @param numbers Array of submittal number strings to search
   * @returns object with prefix, num, and length, e.g SUB-042 becomes { prefix: 'SUB-', num: 42, length: 3 }
   * if no numbers passed or none contained numbers, returns as if we passed 000 { prefix: '', num: 0, length: 3 }
   */
  static getMaxPattern(numbers: string[]): { prefix: string; num: number; length: number } {
    return numbers.reduce(
      (best, number) => {
        const match = number && number.match(/^(?:(.*)-)?(\d+)$/); // only match trailing digits
        if (!match) return best; // skip invalid

        const cur = { prefix: match[1] ?? '', num: parseInt(match[2], 10), length: match[2].length };

        if (!best) return cur;
        if (cur.num !== best.num) return cur.num > best.num ? cur : best;
        return cur.length > best.length ? cur : best; // tie-breaker
      },
      { prefix: '', num: 0, length: 3 } as { prefix: string; num: number; length: number },
    );
  }

  static getNextNumber(numbers: string[]): string {
    const max = this.getMaxPattern(numbers);
    return this.getNumberFromPattern(max.prefix, max.num + 1, max.length);
  }

  static getNumberFromPattern(prefix: string, num: number, padding: number): string {
    return `${prefix}${String(num).padStart(padding, '0')}`;
  }

  static getRevisionPendingStates(revision: SubmittalRevision): WorkflowState[] {
    return isUndefined(revision.completed_state_index) ? revision.states : revision.states.slice(revision.completed_state_index + 1);
  }

  static feedToStatusHistory(feed: SubmittalFeedItemDoc[]): WorkflowStateComplete[] {
    return feed.map(item => ({
      state_id: item.sub_id,
      user_id: item.user_id,
      date: item.date,
    }));
  }

  static renderSubmittalName(doc?: SubmittalDoc, chars?: number, fallback?: string): string {
    let fullName = '';
    if (!doc && !fallback) {
      return '';
    }

    if (!doc) {
      fullName = fallback;
    } else {
      fullName = ` #${doc.code} - ${doc.title}`;
    }

    if (chars && fullName.length > chars) {
      return `${fullName.substring(0, chars)}...`;
    }

    return fullName;
  }

  static getUnacceptedFileFeedItems(revision: SubmittalRevision, feed: SubmittalFeedItemDoc[]): SubmittalFeedItemDoc[] {
    return this.getUnacceptedFeedItemsBySub(revision, feed, FeedItemSubType.FILE);
  }

  /** PHOTO feed items for this revision that are not in accepted_answers. */
  static getUnacceptedPhotoFeedItems(revision: SubmittalRevision, feed: SubmittalFeedItemDoc[]): SubmittalFeedItemDoc[] {
    return this.getUnacceptedFeedItemsBySub(revision, feed, FeedItemSubType.PHOTO);
  }

  private static getUnacceptedFeedItemsBySub(revision: SubmittalRevision, feed: SubmittalFeedItemDoc[], sub: FeedItemSubType): SubmittalFeedItemDoc[] {
    const accepted = new Set(revision.accepted_answers || []);
    return feed.filter(item => item.sub === sub && !item.deleted_date && !accepted.has(item._id));
  }
}
